import {
  FACE_KEYS,
  type SaveEditAreaInput,
  saveEditAreaInputSchema,
  surfaceKindMismatch,
} from '@repo/api-contracts';
import type { EditAreaDraftState } from '../model/edit-area-draft-types';
import { COMPOSITE_ASSET_KINDS, compositeSlotKey } from './composite-asset';
import { hasMeasurementDrift } from './measurement-drift';
import { unsupportedSaveMessage } from './unsupported-definition';

export interface EditAreaValidationIssue {
  readonly path: readonly PropertyKey[];
  readonly message: string;
}
export type AssembleEditAreaResult =
  | { readonly success: true; readonly data: SaveEditAreaInput }
  | { readonly success: false; readonly issues: readonly EditAreaValidationIssue[] };

/**
 * 면마다 표면 1개와 실측 종류·표면 타입 1:1 을 검사한다. 잠금 사유는 서버 상태에서만 계산되므로
 * 초안이 어떤 경로로 어긋나도 저장 경계가 서버와 같은 문구로 막는다.
 */
const surfaceIssues = (
  state: Pick<EditAreaDraftState, 'faces' | 'measurementKind'>,
): EditAreaValidationIssue[] =>
  FACE_KEYS.flatMap((face) => {
    const surfaces = state.faces[face]?.surfaces ?? [];
    const count: EditAreaValidationIssue[] =
      surfaces.length > 1
        ? [
            {
              path: ['definition', 'faces', face, 'surfaces'],
              message: '면마다 표면은 1개만 둘 수 있습니다. 나머지 표면을 삭제해 주세요.',
            },
          ]
        : [];
    const kind = surfaces.flatMap((surface, index): EditAreaValidationIssue[] => {
      const message = surfaceKindMismatch(surface.type, state.measurementKind);
      return message === null
        ? []
        : [{ path: ['definition', 'faces', face, 'surfaces', index], message }];
    });
    return [...count, ...kind];
  });

/** 합성 업로드가 진행 중이거나 실패로 남은 면. 사진 작업과 따로 그 면 합성 설정 자리로 안내한다. */
const compositeOperationIssues = (
  state: Pick<EditAreaDraftState, 'operations'>,
): EditAreaValidationIssue[] =>
  FACE_KEYS.filter((face) =>
    COMPOSITE_ASSET_KINDS.some((kind) => state.operations[compositeSlotKey(face, kind)]),
  ).map((face) => ({
    path: ['definition', 'faces', face, 'composite'],
    message: '합성 에셋 업로드가 진행 중이거나 해결하지 않은 오류가 있습니다.',
  }));

/** 전체 저장 경계. 미완성 사진은 완성 계약으로 단언하지 않고 공용 schema로 검사한다. */
export const assembleEditAreaInput = (state: EditAreaDraftState): AssembleEditAreaResult => {
  const issues: EditAreaValidationIssue[] = [];
  const unsupported = unsupportedSaveMessage(state.unsupportedReasons);
  if (unsupported !== null) issues.push({ path: ['definition'], message: unsupported });
  if (Object.keys(state.pendingInputs).length)
    issues.push({ path: [], message: '입력 중인 값을 먼저 적용해 주세요.' });
  if (state.measurement === null)
    issues.push({
      path: [],
      message: '상품 실측이 없습니다. 상품 수정 화면에서 실측 규격을 먼저 등록해 주세요.',
    });
  else if (hasMeasurementDrift(state.faces, state.measurement, state.measurementKind))
    issues.push({
      path: [],
      message: "상품 실측이 바뀌었습니다. '실측 갱신'을 누른 뒤 저장해 주세요.",
    });
  issues.push(...surfaceIssues(state));
  const readiness = state.getPhotoReadiness();
  if (readiness.hasPending || readiness.hasErrors)
    issues.push({
      path: ['definition', 'colors'],
      message: '사진 업로드가 진행 중이거나 해결하지 않은 오류가 있습니다.',
    });
  issues.push(...compositeOperationIssues(state));
  const result = saveEditAreaInputSchema.safeParse({
    productId: state.draft.productId,
    definition: { components: state.components, faces: state.faces, colors: state.draft.colors },
  });
  if (!result.success)
    issues.push(...result.error.issues.map(({ path, message }) => ({ path, message })));
  if (issues.length || !result.success) return { success: false, issues };
  return { success: true, data: result.data };
};
